import React, { useEffect, useRef, useState } from 'react';
import 'leaflet/dist/leaflet.css';

/**
 * I gruppi scout dell'edizione su una mappa OpenStreetMap.
 *
 * Leaflet arriva con un import dinamico: e' la libreria piu' pesante della pagina e serve solo a
 * chi apre questo pannello, quindi sta fuori dal bundle principale.
 *
 * I punti sono <code>circleMarker</code>, cerchi disegnati, e non i marker con l'icona: l'icona di
 * default di Leaflet e' un file PNG che i bundler sistematicamente non trovano, e un cerchio grande
 * quanto il gruppo dice comunque di piu' di uno spillo tutto uguale.
 *
 * Le tile arrivano da internet. Sulla macchina in sede, senza rete, restano i cerchi su uno sfondo
 * vuoto: la posizione relativa dei gruppi si legge lo stesso, e l'elenco qui accanto non dipende
 * dalla mappa.
 */
/** Il raggio, in pixel, del pallino del gruppo piu' numeroso: e' anche il margine di sicurezza. */
const RAGGIO_MASSIMO = 18;

const MappaGruppi = ({ punti }) => {
  const contenitore = useRef(null);
  const [rotta, setRotta] = useState(false);

  useEffect(() => {
    if (punti.length === 0) return undefined;

    let mappa;
    let annullato = false;

    (async () => {
      try {
        const L = (await import('leaflet')).default;
        if (annullato || !contenitore.current) return;

        mappa = L.map(contenitore.current, { scrollWheelZoom: false });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 18,
          // L'attribuzione non e' un vezzo: le condizioni d'uso delle tile di OSM la richiedono.
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        }).addTo(mappa);

        const massimo = Math.max(...punti.map(p => p.giocatori), 1);

        punti.forEach(punto => {
          L.circleMarker([punto.lat, punto.lng], {
            radius: RAGGIO_MASSIMO - 13 * (1 - Math.sqrt(punto.giocatori / massimo)),
            color: '#229954',
            fillColor: '#27ae60',
            fillOpacity: 0.55,
            weight: 2
          })
            .bindPopup(`<strong>${punto.nome}</strong><br/>${punto.giocatori} giocatori, in ${punto.squadre} squadre`)
            .addTo(mappa);
        });

        // Il rettangolo piu' piccolo che contiene tutti i pallini: nessun tetto allo zoom, che
        // terrebbe la mappa larga anche quando i gruppi stanno tutti in due province. L'unico
        // margine e' il raggio del pallino piu' grande, altrimenti i punti di bordo verrebbero
        // tagliati a meta' dal riquadro.
        const inquadra = () => {
          const confini = L.latLngBounds(punti.map(p => [p.lat, p.lng]));

          // Un gruppo solo, o tutti nello stesso posto: il rettangolo e' un punto, e fitBounds
          // andrebbe allo zoom massimo, cioe' sui tetti delle case.
          if (confini.getNorth() === confini.getSouth() && confini.getEast() === confini.getWest())
            mappa.setView(confini.getCenter(), 10);
          else
            mappa.fitBounds(confini, { padding: [RAGGIO_MASSIMO, RAGGIO_MASSIMO] });
        };

        inquadra();

        // Il pannello si e' appena aperto: Leaflet puo' aver misurato il contenitore prima che il
        // browser lo disegnasse, e il risultato e' la classica mappa grigia con le tile a meta'.
        // Un giro di rendering dopo le misure sono quelle vere, e l'inquadratura va rifatta su
        // quelle: il rettangolo piu' piccolo dipende da quanto e' grande il riquadro.
        requestAnimationFrame(() => {
          mappa.invalidateSize();
          inquadra();
        });
      } catch (errore) {
        console.error('Leaflet non si e" caricato', errore);
        if (!annullato) setRotta(true);
      }
    })();

    return () => {
      annullato = true;
      if (mappa) mappa.remove();
    };
  }, [punti]);

  if (punti.length === 0)
    return (
      <div className="mappa-gruppi">
        <p className="mappa-avviso">Nessun gruppo scout con coordinate: la mappa non ha punti da mostrare.</p>
      </div>
    );

  if (rotta)
    return (
      <div className="mappa-gruppi">
        <p className="mappa-avviso">La mappa non si e' caricata. L'elenco qui accanto vale lo stesso.</p>
      </div>
    );

  return <div className="mappa-gruppi" ref={contenitore} data-testid="mappa-gruppi" />;
};

export default MappaGruppi;
